import React, { useState } from 'react';

interface Step4IndustryProps {
  industry: string;
  role: string;
  onUpdate: (industry: string, role: string) => void;
  onComplete: () => void;
  onBack: () => void;
}

type PolicyType = 'privacy' | 'terms' | null;

const INDUSTRIES = [
  'Accounting',
  'Advertising & Marketing',
  'Aerospace & Defense',
  'Agriculture & Farming',
  'Architecture & Planning',
  'Automotive',
  'Banking',
  'Biotechnology',
  'Broadcasting & Media',
  'Business Services',
  'Chemicals',
  'Civil Engineering',
  'Construction',
  'Consulting',
  'Consumer Electronics',
  'Consumer Goods',
  'Cybersecurity',
  'Design',
  'E-commerce',
  'Education',
  'Energy & Utilities',
  'Entertainment',
  'Environmental Services',
  'Fashion & Apparel',
  'Financial Services',
  'Food & Beverage',
  'Gaming',
  'Government & Public Administration',
  'Healthcare',
  'Hospitality & Tourism',
  'Human Resources',
  'Information Technology',
  'Insurance',
  'Investment Management',
  'Legal Services',
  'Logistics & Supply Chain',
  'Manufacturing',
  'Maritime',
  'Mining',
  'Nonprofit & NGO',
  'Oil & Gas',
  'Pharmaceuticals',
  'Public Relations',
  'Publishing',
  'Real Estate',
  'Research & Development',
  'Retail',
  'Sports & Recreation',
  'Telecommunications',
  'Transportation',
  'Travel & Tourism',
  'Venture Capital & Private Equity',
  'Wholesale',
  'Other'
];

const PRIVACY_POLICY = `Privacy Policy
Last Updated: 08/11/2025

JobVital ('we', 'our', 'us') respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your information when you use our platform or apply for jobs.

1. Who We Are
JobVital is an early-stage platform designed to connect jobseekers with employers. We sometimes post jobs on behalf of third-party hiring companies ('Clients'). When you apply to a job, we may share your details with the hiring company if you are a potential match.

2. Information We Collect
When you sign up or apply through JobVital, we may collect:
- Name and contact details (email address, phone number)
- Uploaded CV/resume and application information
- Country and role preferences
- Education, experience, and skills
- Any other details you voluntarily provide

3. How We Use Your Information
We use your data to:
- Assess your suitability for roles
- Share your details with hiring companies when appropriate
- Send you other suitable job opportunities
- Communicate with you about your account or updates
- Improve our platform and matching system

We will never sell your personal data.

4. When We Share Your Information
We may share your data with:
- Hiring companies that are advertising jobs through us
- Service providers that help us run the platform (for hosting, email delivery, etc.)
- Legal authorities if required by law

All partners are expected to handle your data securely and only for recruitment purposes.

5. Data Retention & Deletion
You can delete your account at any time. When you do, all personal information, including your CV, profile, and application history, will be permanently deleted from our systems within 30 days.

6. Your Rights
You can:
- Access your data
- Update or correct your details
- Request permanent deletion of your account
- Withdraw consent to receive emails or job alerts

To exercise these rights, contact info@jobvital.co.uk

7. Security
We use secure systems and reasonable measures to protect your information from unauthorized access, disclosure, or loss.

8. Updates
We may update this policy as we develop our platform. When we do, we'll change the 'Last Updated' date and post the new version.

9. Contact
If you have any privacy questions, contact us at:
info@jobvital.co.uk`;

const TERMS_AND_CONDITIONS = `JobVital Terms & Conditions
Last Updated: 08/11/2025

These Terms & Conditions ('Terms') outline the agreement between you ('the user', 'you') and the JobVital team ('JobVital', 'we', 'our', 'us') regarding your use of the JobVital platform. By using our platform or clicking 'Submit', you agree to these Terms.

1. Use of the Platform
JobVital allows you to discover and apply to job opportunities. Some jobs are posted directly by JobVital on behalf of third-party companies ('Clients'). You agree to provide accurate and truthful information when creating an account or submitting an application. You must be at least 18 years old.

2. Job Applications
Applying through JobVital does not guarantee an interview or employment. JobVital may share your CV and details with employers when we consider you a good match. If not, we may continue to send you other suitable job opportunities. JobVital is not responsible for hiring outcomes or communication delays from employers.

3. Data & Privacy
By using our platform, you consent to the collection and processing of your data in accordance with our Privacy Policy. You can delete your account at any time to permanently remove your data.

4. Intellectual Property
All content, branding, and materials on the JobVital platform belong to JobVital. You may not copy, reproduce, or distribute them without permission.

5. Liability Disclaimer
JobVital is an intermediary that connects candidates with employers. We make no guarantees about job availability, suitability, or hiring outcomes. We are not liable for any loss, damage, or inconvenience arising from the use of our platform or from decisions made by hiring companies.

6. Account Termination
We reserve the right to suspend or remove any account that violates these Terms or misuses the platform.

7. Updates
We may update these Terms from time to time. Continued use of the platform after changes means you accept the updated version.

8. Contact
For questions about these Terms, contact: info@jobvital.co.uk`;

export const Step4Industry: React.FC<Step4IndustryProps> = ({
  industry,
  role,
  onUpdate,
  onComplete,
  onBack,
}) => {
  const [errors, setErrors] = useState({ industry: '', role: '', agreement: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [openPolicy, setOpenPolicy] = useState<PolicyType>(null);

  const handleComplete = () => {
    const newErrors = { industry: '', role: '', agreement: '' };

    if (!industry.trim()) {
      newErrors.industry = 'Industry is required';
    }

    if (!role.trim()) {
      newErrors.role = 'Role is required';
    } else if (role.trim().length < 2) {
      newErrors.role = 'Role must be at least 2 characters';
    }

    if (!agreedToTerms) {
      newErrors.agreement = 'You must agree to the privacy policy and terms & conditions';
    }

    setErrors(newErrors);

    if (!newErrors.industry && !newErrors.role && !newErrors.agreement) {
      onComplete();
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Tell us about your career</h1>
      <div className="space-y-4 md:space-y-6">
        <div>
          <label htmlFor="industry" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Industry <span className="text-red-500">*</span>
          </label>
          <select
            id="industry"
            value={industry}
            onChange={(e) => {
              setErrors((prev) => ({ ...prev, industry: '' }));
              onUpdate(e.target.value, role);
            }}
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${
              errors.industry
                ? 'border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
            }`}
          >
            <option value="">Select an industry</option>
            {INDUSTRIES.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
          {errors.industry && <p className="mt-1 text-xs md:text-sm text-red-600">{errors.industry}</p>}
        </div>

        <div>
          <label htmlFor="role" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
            Which Role are you applying for? <span className="text-red-500">*</span>
          </label>
          <input
            id="role"
            type="text"
            value={role}
            onChange={(e) => {
              setErrors((prev) => ({ ...prev, role: '' }));
              onUpdate(industry, e.target.value);
            }}
            placeholder="e.g., Software Engineer, Product Manager"
            className={`w-full px-3 md:px-4 py-2.5 md:py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm md:text-base ${
              errors.role
                ? 'border-red-500 focus:ring-red-200'
                : 'border-gray-300 focus:ring-blue-200 focus:border-blue-500'
            }`}
          />
          {errors.role && <p className="mt-1 text-xs md:text-sm text-red-600">{errors.role}</p>}
        </div>

        {/* Agreement Checkbox */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-4">
          <div className="flex items-start gap-2 md:gap-3">
            <input
              type="checkbox"
              id="agreement"
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked);
                if (e.target.checked) {
                  setErrors((prev) => ({ ...prev, agreement: '' }));
                }
              }}
              className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer flex-shrink-0"
            />
            <label htmlFor="agreement" className="text-xs md:text-sm text-gray-700 cursor-pointer select-none leading-tight">
              <span className="text-red-500">* </span>
              I have read and agreed to the{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenPolicy('privacy');
                }}
                className="text-blue-600 hover:text-blue-800 underline font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
              >
                privacy policy
              </button>
              {' '}and{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setOpenPolicy('terms');
                }}
                className="text-blue-600 hover:text-blue-800 underline font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
              >
                terms & conditions
              </button>
            </label>
          </div>
          {errors.agreement && <p className="mt-2 text-xs md:text-sm text-red-600 ml-6 md:ml-7">{errors.agreement}</p>}
        </div>

        <div className="flex gap-3 md:gap-4">
          <button
            onClick={onBack}
            className="flex-1 bg-white text-gray-700 py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
          >
            Back
          </button>
          <button
            onClick={handleComplete}
            className="flex-1 bg-blue-600 text-white py-2.5 md:py-3 px-4 md:px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
          >
            Submit
          </button>
        </div>
      </div>

      {/* Policy Modal */}
      {openPolicy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 md:p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] max-h-[90dvh] flex flex-col">
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                {openPolicy === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions'}
              </h2>
              <button
                onClick={() => setOpenPolicy(null)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
                aria-label="Close"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 md:p-6 overflow-y-auto flex-1">
              <pre className="whitespace-pre-wrap text-xs md:text-sm text-gray-700 font-sans">
                {openPolicy === 'privacy' ? PRIVACY_POLICY : TERMS_AND_CONDITIONS}
              </pre>
            </div>
            <div className="p-4 md:p-6 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setOpenPolicy(null)}
                className="w-full bg-blue-600 text-white py-2.5 md:py-3 px-6 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm md:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

